import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { extractFunctionErrorMessage } from '@/features/financials/hooks';

/**
 * AI daily brief that powers the banner at the top of /dashboard for every
 * member (not just super_admin). The `daily-brief-ai` Edge Function reads
 * whatever the caller is allowed to see — tasks, calendar, PTO, sales,
 * inventory, marketing numbers — and asks Claude to write a role-aware
 * morning briefing.
 *
 * Cached for 30 minutes and keyed by calendar day so a morning load stays
 * cheap, but a Refresh click still re-runs the estimate.
 */

export type BriefUrgency = 'high' | 'medium' | 'low';

export interface DailyBriefFigure {
  label: string;
  value: string;
  detail: string | null;
}

export interface DailyBriefAlert {
  label: string;
  detail: string;
  urgency: BriefUrgency;
}

export interface DailyBriefTask {
  label: string;
  detail: string | null;
  urgency: BriefUrgency | null;
}

export interface DailyBrief {
  headline: string;
  summary: string;
  focus: string[];
  figures: DailyBriefFigure[];
  alerts: DailyBriefAlert[];
  tasks: DailyBriefTask[];
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function useDailyBrief() {
  const { profile, loading } = useAuth();
  const enabled = !loading && !!profile && profile.role !== 'external_lender';

  return useQuery({
    queryKey: ['daily-brief', profile?.id ?? 'noop', todayKey()],
    enabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        brief?: DailyBrief;
        persona?: string;
        model?: string;
        error?: string;
      }>('daily-brief-ai', { body: {} });
      if (error) {
        const msg = await extractFunctionErrorMessage(error);
        throw new Error(msg);
      }
      if (!data?.ok || !data.brief) {
        throw new Error(data?.error ?? 'Daily brief failed');
      }
      return {
        brief: data.brief,
        persona: data.persona ?? null,
        model: data.model ?? null,
      };
    },
  });
}
